import Mention from "@tiptap/extension-mention";
import classNames from "classnames";
import EmojiPicker, { EmojiStyle, Theme } from "emoji-picker-react";
import useUpdateEffect from "hooks/useUpdateEffect";
import { compact, trim } from "lodash-es";
import React, {
  MouseEvent,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import FileExtension from "tiptap/FileExtension";
import { mentionSuggestion } from "tiptap/MentionSuggestion";
import { UserInfoProjectorImpl } from "types/api/userInfoProjectorImpl";
import {
  convertHtmlCommentToMarkDown,
  getMentionUserIds,
} from "utils/commentUtil";
import { validateFileForUpload } from "utils/fileUtils";

import ACTrigger from "ac-components/ACTrigger";
import ACButton from "ac-components/ac-button/ACButton";
import ACEditor, {
  ACEditorProps,
  ACEditorRef,
} from "ac-components/ac-editor/ACEditor";
import AttachmentIcon from "ac-components/ac-icons/AttachmentIcon";
import BoldIcon from "ac-components/ac-icons/BoldIcon";
import H1Icon from "ac-components/ac-icons/H1Icon";
import H2Icon from "ac-components/ac-icons/H2Icon";
import H3Icon from "ac-components/ac-icons/H3Icon";
import ImageIcon from "ac-components/ac-icons/ImageIcon";
import ItalicIcon from "ac-components/ac-icons/ItalicIcon";
import OrderedListIcon from "ac-components/ac-icons/OrderedListIcon";
import SmileyIcon from "ac-components/ac-icons/SmileyIcon";
import TableIcon from "ac-components/ac-icons/TableIcon";
import UnderlineIcon from "ac-components/ac-icons/UnderlineIcon";
import UnorderedListIcon from "ac-components/ac-icons/UnorderedListIcon";
import ACTableBubbleMenu from "ac-components/ac-table-bubble-menu/ACTableBubbleMenu";

import "./ACCommentEditor.less";
import { ToolBarButton, prefixCls } from "./common";
import { LinkPopover } from "./components/link-popover/LinkPopover";

export type OnSumbitProps = {
  htmlText: string;
  markdownText: string;
  mentionUserIds: number[];
};

export type ACCommentEditorProps = {
  initialValue?: string;
  onSubmit?: (props: OnSumbitProps) => Promise<void>;
  min?: number;
  defaultFocus?: boolean;
  onCancel?: () => void;
  onChange?: ACEditorProps["onChange"];
  submitText?: string;
  users?: UserInfoProjectorImpl[];
  showFooter?: boolean;
  placeholder?: string;
  id?: string;
  allowMention?: boolean;
  allowAttachments?: boolean;
  allowImages?: boolean;
};

type ACCommentEditorRefType = {
  setValue: (value: string) => void;
};

const ACCommentEditor = forwardRef<
  ACCommentEditorRefType,
  ACCommentEditorProps
>(
  (
    {
      initialValue,
      min = 3,
      onSubmit,
      defaultFocus = false,
      onCancel,
      onChange,
      submitText = "Send",
      users,
      showFooter = true,
      placeholder = "Enter Comment",
      id,
      allowMention = true,
      allowAttachments = true,
      allowImages,
    },
    ref,
  ) => {
    const [focused, setFocused] = useState(defaultFocus);
    const [submitting, setSubmitting] = useState(false);
    const [value, setValue] = useState(initialValue);
    const editorRef = useRef<ACEditorRef>(null);

    useImperativeHandle(ref, () => ({
      setValue: (value: string) => setValue(value),
    }));

    useUpdateEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const hasMinLimit = useMemo(() => {
      const _htmlDoc = document.createElement("div");
      _htmlDoc.innerHTML = value || "";
      const hasContent = trim(_htmlDoc.innerText).length >= min;
      return (
        hasContent ||
        value?.includes("<img") ||
        value?.includes("<file-attachment")
      );
    }, [value, min]);

    const addImage = (e: MouseEvent<HTMLDivElement>) => {
      const isImage = e.currentTarget.dataset.type === "image";
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      if (isImage) {
        input.setAttribute("accept", "image/*");
      }
      input.onchange = (e) => {
        if (input.files?.length && editorRef.current?.editor) {
          const editor = editorRef.current.editor;
          let file: File | undefined = input.files[0];
          file = file ? validateFileForUpload([file]) : undefined;
          if (!file) {
            return;
          }
          if (file.type.indexOf("image") > -1) {
            editor.chain().focus().insertImageAttachment(file).run();
          } else {
            editor.chain().focus().insertFileAttachment(file).run();
          }
          editor.commands.setTextSelection(
            editor.state.selection.$from.pos + 2,
          );
        }
      };
      input.click();
    };

    const onSubmitClick = async () => {
      setSubmitting(true);
      try {
        await onSubmit?.({
          htmlText: value || "",
          markdownText: convertHtmlCommentToMarkDown(
            value,
          ) as unknown as string,
          mentionUserIds: getMentionUserIds(value),
        });
        setValue(undefined);
      } catch (e) {
        console.error("Error while sumbitting comment", e);
      } finally {
        setSubmitting(false);
      }
    };
    return (
      <div
        className={classNames(`${prefixCls}-wrapper`, { focused, submitting })}
        onClick={(e) => {
          setFocused(true);
        }}
      >
        <div className={`${prefixCls}`}>
          <ACEditor
            id={id}
            disabled={submitting}
            value={value}
            onChange={(value) => {
              setValue(value);
              if (onChange) {
                onChange(value);
              }
            }}
            bubbleMenu={false}
            onFocus={() => {
              setFocused(true);
            }}
            extensions={getAdditionalExtions({
              users,
              allowMention,
              allowAttachments,
            })}
            placeholder={placeholder}
            ref={editorRef}
            allowAttachments={allowAttachments}
            allowImages={allowImages ?? allowAttachments}
            slotBeforeRender={(editor) => (
              <div className={`${prefixCls}-tool-bar`}>
                <ToolBarButton
                  onClick={() => {
                    editor.chain().focus().toggleHeading({ level: 1 }).run();
                  }}
                  active={editor.isActive("heading", { level: 1 })}
                  title="Heading 1"
                >
                  <H1Icon />
                </ToolBarButton>
                <ToolBarButton
                  onClick={() => {
                    editor.chain().focus().toggleHeading({ level: 2 }).run();
                  }}
                  active={editor.isActive("heading", { level: 2 })}
                  title="Heading 2"
                >
                  <H2Icon />
                </ToolBarButton>
                <ToolBarButton
                  onClick={() => {
                    editor.chain().focus().toggleHeading({ level: 3 }).run();
                  }}
                  active={editor.isActive("heading", { level: 3 })}
                  title="Heading 3"
                >
                  <H3Icon />
                </ToolBarButton>
                <ToolBarButton
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  active={editor.isActive("bold")}
                  title="Bold"
                >
                  <BoldIcon />
                </ToolBarButton>
                <ToolBarButton
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  active={editor.isActive("italic")}
                  title="Italic"
                >
                  <ItalicIcon />
                </ToolBarButton>
                <ToolBarButton
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  active={editor.isActive("underline")}
                  title="Underline"
                >
                  <UnderlineIcon />
                </ToolBarButton>
                <ToolBarButton
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  active={editor.isActive("bulletList")}
                  title="Bullet list"
                >
                  <UnorderedListIcon />
                </ToolBarButton>
                <ToolBarButton
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  active={editor.isActive("orderedList")}
                  title="Numbered list"
                >
                  <OrderedListIcon />
                </ToolBarButton>
                {(allowImages ?? allowAttachments) && (
                  <ToolBarButton
                    onClick={addImage}
                    type="image"
                    title="Add image"
                  >
                    <ImageIcon />
                  </ToolBarButton>
                )}
                {allowAttachments && (
                  <ToolBarButton onClick={addImage} title="Attachments">
                    <AttachmentIcon />
                  </ToolBarButton>
                )}
                <ToolBarButton
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                      .run()
                  }
                  title="Table"
                >
                  <TableIcon />
                </ToolBarButton>
                <ACTrigger
                  popup={
                    <EmojiPicker
                      skinTonesDisabled
                      theme={Theme.LIGHT}
                      emojiStyle={EmojiStyle.NATIVE}
                      onEmojiClick={(e) =>
                        editor.commands.insertContent(e.emoji)
                      }
                    />
                  }
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <ToolBarButton>
                    <SmileyIcon />
                  </ToolBarButton>
                </ACTrigger>
                <LinkPopover editor={editor} />
              </div>
            )}
            slotAfterRender={(editor) => <ACTableBubbleMenu editor={editor} />}
          />
        </div>
        {focused && showFooter && (
          <div className={`${prefixCls}-footer`}>
            <ACButton
              type="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (onCancel) {
                  onCancel();
                } else {
                  setValue("");
                  setFocused(false);
                }
              }}
              disabled={submitting}
            >
              Cancel
            </ACButton>
            <ACButton
              disabled={!hasMinLimit || submitting}
              size="sm"
              onClick={onSubmitClick}
            >
              {submitText}
            </ACButton>
          </div>
        )}
      </div>
    );
  },
);

export default ACCommentEditor;

export const getAdditionalExtions = ({
  users = [],
  allowMention = true,
  allowAttachments = true,
}: {
  users?: UserInfoProjectorImpl[];
  allowMention?: boolean;
  allowAttachments?: boolean;
}) => {
  return compact([
    allowAttachments ? FileExtension : undefined,
    allowMention
      ? Mention.configure({
          HTMLAttributes: {
            class: "mention",
          },
          suggestion: mentionSuggestion({ users }),
        })
      : undefined,
  ]);
};
